import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@shared/api';
import { dateTime, idr } from '@shared/format';
import { refreshBalance } from '@shared/hooks';
import { CopyIcon } from '../components/CopyIcon';
import { QrCode, downloadQr } from '../components/QrCode';
import { TopupSuccess } from '../components/TopupSuccess';
import { Alert, Badge, Card, Icon, Loading, PageHeader } from '../components/ui';
import '../styles/billing.css';

interface CheckoutView {
  external_payment_id: string;
  amount: number;
  bonus_amount: number;
  credited_amount: number;
  method: string;
  status: string;
  qr_string: string | null;
  va_number: string | null;
  hosted_url: string | null;
  expires_at: string | null;
  created_at: string;
  paid_at: string | null;
  can_simulate: boolean;
}

const POLL_MS = 4_000;
// ask Xendit every 4th
const REFRESH_EVERY = 4;

const BANKS: Record<string, { name: string; steps: Array<{ title: string; items: string[] }> }> = {
  VA_BNI: {
    name: 'BNI',
    steps: [
      {
        title: 'wondr by BNI / BNI Mobile Banking',
        items: ['Pilih menu Transfer, lalu Virtual Account.', 'Masukkan nomor virtual account di atas.', 'Periksa nama dan jumlah tagihan, lalu konfirmasi dengan PIN.'],
      },
      {
        title: 'ATM BNI',
        items: ['Pilih Menu Lainnya, lalu Transfer.', 'Pilih Virtual Account Billing dan masukkan nomor virtual account.', 'Periksa tagihan, lalu konfirmasi.'],
      },
      {
        title: 'Bank lain',
        items: ['Pilih transfer antarbank atau transfer online ke BNI (kode 009).', 'Masukkan nomor virtual account sebagai nomor rekening tujuan.', 'Isi jumlah tepat sesuai tagihan, lalu konfirmasi.'],
      },
    ],
  },
  VA_MANDIRI: {
    name: 'Mandiri',
    steps: [
      {
        title: "Livin' by Mandiri",
        items: ['Pilih menu Bayar, lalu cari penyedia jasa Xendit.', 'Masukkan nomor virtual account di atas.', 'Periksa nama dan jumlah tagihan, lalu konfirmasi dengan PIN.'],
      },
      {
        title: 'ATM Mandiri',
        items: ['Pilih Bayar/Beli, Lainnya, lalu Multipayment.', 'Masukkan kode perusahaan, yaitu 5 digit pertama nomor virtual account.', 'Masukkan nomor virtual account lengkap, periksa tagihan, lalu konfirmasi.'],
      },
      {
        title: 'Bank lain',
        items: ['Pilih transfer antarbank atau transfer online ke Mandiri (kode 008).', 'Masukkan nomor virtual account sebagai nomor rekening tujuan.', 'Isi jumlah tepat sesuai tagihan, lalu konfirmasi.'],
      },
    ],
  },
  VA_BCA: {
    name: 'BCA',
    steps: [
      {
        title: 'BCA mobile (m-BCA)',
        items: ['Pilih m-Transfer, lalu BCA Virtual Account.', 'Masukkan nomor virtual account di atas.', 'Periksa nama dan jumlah tagihan, lalu konfirmasi dengan PIN.'],
      },
      {
        title: 'KlikBCA',
        items: ['Pilih Transfer Dana, lalu Transfer ke BCA Virtual Account.', 'Masukkan nomor virtual account.', 'Periksa tagihan, lalu konfirmasi dengan KeyBCA.'],
      },
      {
        title: 'ATM BCA',
        items: ['Pilih Transaksi Lainnya, Transfer, lalu ke Rek BCA Virtual Account.', 'Masukkan nomor virtual account.', 'Periksa tagihan, lalu konfirmasi.'],
      },
    ],
  },
};

const QRIS_STEPS = [
  'Buka aplikasi bank atau e-wallet yang mendukung QRIS, misalnya BCA, Mandiri, BRI, GoPay, OVO, DANA, atau ShopeePay.',
  'Pindai kode QR. Bila membayar dari HP ini, unduh QR lalu unggah dari galeri di aplikasi pembayaran.',
  'Periksa jumlahnya, lalu konfirmasi pembayaran.',
];

const METHOD_LABEL: Record<string, string> = {
  QRIS: 'QRIS',
  VA_BCA: 'Virtual Account BCA',
  VA_BNI: 'Virtual Account BNI',
  VA_MANDIRI: 'Virtual Account Mandiri',
  EWALLET_GOPAY: 'GoPay',
  EWALLET_OVO: 'OVO',
  CARD: 'Kartu kredit/debit',
};

/** 8808123412341234 -> groups of 4. */
const groupDigits = (value: string) => value.replace(/(.{4})(?=.)/g, '$1 ');

function useCountdown(expiresAt: string | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);
  return expiresAt ? Math.max(0, new Date(expiresAt).getTime() - now) : null;
}

function clock(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export function Checkout() {
  const { externalId = '' } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState<CheckoutView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [celebrate, setCelebrate] = useState<number | null>(null);
  const status = useRef<string | null>(null);
  const path = `/v1/payments/${encodeURIComponent(externalId)}`;

  const apply = useCallback(async (next: CheckoutView) => {
    const was = status.current;
    status.current = next.status;
    setView(next);
    // just turned paid
    if (next.status === 'PAID' && was === 'PENDING') {
      refreshBalance();
      const balance = await api<{ available_balance: number }>('/v1/wallet/balance').catch(() => null);
      setCelebrate(balance?.available_balance ?? -1);
    }
  }, []);

  const load = useCallback(
    async (askGateway: boolean) => {
      try {
        if (askGateway) await api(`${path}/refresh`, { method: 'POST' }).catch(() => null);
        await apply(await api<CheckoutView>(path));
        setError(null);
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [path, apply],
  );

  useEffect(() => {
    status.current = null;
    setView(null);
    void load(true);
  }, [load]);

  // poll while pending
  useEffect(() => {
    if (view?.status !== 'PENDING') return;
    let tick = 0;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(++tick % REFRESH_EVERY === 0);
    }, POLL_MS);
    const onVisible = () => document.visibilityState === 'visible' && void load(true);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [view?.status, load]);

  const remaining = useCountdown(view?.status === 'PENDING' ? view.expires_at : null);
  const timeUp = remaining === 0;

  // expiry is server-side
  useEffect(() => {
    if (timeUp) void load(true);
  }, [timeUp, load]);

  const closeCelebrate = useCallback(() => {
    setCelebrate(null);
    navigate('/wallet');
  }, [navigate]);

  if (!view) {
    if (error) {
      return (
        <div className="co-page">
          <Alert kind="error">{error}</Alert>
          <Link className="btn" to="/wallet">
            <Icon name="left" size={15} />
            Kembali ke saldo
          </Link>
        </div>
      );
    }
    return <Loading label="Memuat tagihan" />;
  }

  const check = async () => {
    setChecking(true);
    setNotice(null);
    await load(true);
    setChecking(false);
    if (status.current === 'PENDING') setNotice('Pembayaran belum kami terima. Bila Anda baru membayar, tunggu sebentar; halaman ini diperbarui otomatis.');
  };

  const simulate = async () => {
    setSimulating(true);
    setNotice(null);
    try {
      await api(`${path}/simulate`, { method: 'POST' });
      await load(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSimulating(false);
    }
  };

  const pending = view.status === 'PENDING';
  const bank = BANKS[view.method];
  const method = METHOD_LABEL[view.method] ?? view.method;

  return (
    <div className="co-page">
      {celebrate !== null && <TopupSuccess payment={view} balance={celebrate >= 0 ? celebrate : null} onClose={closeCelebrate} />}

      <PageHeader
        title={pending ? 'Selesaikan pembayaran' : 'Detail pembayaran'}
        subtitle={pending ? 'Saldo bertambah otomatis begitu pembayaran diterima. Halaman ini tidak perlu dimuat ulang.' : undefined}
        actions={
          <Link className="btn" to="/wallet">
            <Icon name="left" size={15} />
            Kembali ke saldo
          </Link>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}
      {notice && <Alert kind="info">{notice}</Alert>}

      <div className="co-grid">
        <Card framed className="co-pay">
          <div className="co-strip">
            <div>
              <span className="co-strip-label">Total bayar</span>
              <span className="co-strip-amount">
                {idr(view.amount)}
                {pending && <CopyIcon value={String(view.amount)} label="Salin jumlah" />}
              </span>
            </div>
            {pending && remaining !== null && (
              <span className={remaining < 5 * 60_000 ? 'co-timer hot' : 'co-timer'} role="timer" aria-label="Sisa waktu bayar">
                <span className="co-timer-label">Sisa waktu</span>
                <strong>{timeUp ? 'Habis' : clock(remaining)}</strong>
              </span>
            )}
          </div>

          {view.status === 'PAID' ? (
            <Outcome icon="success" tone="good" title="Pembayaran diterima" text={`Saldo Anda sudah bertambah ${idr(view.credited_amount)}.`}>
              <Link className="btn btn-primary" to="/wallet">
                Lihat saldo
              </Link>
            </Outcome>
          ) : view.status === 'EXPIRED' || view.status === 'FAILED' ? (
            <Outcome
              icon="warn"
              tone="warn"
              title={view.status === 'EXPIRED' ? 'Tagihan kedaluwarsa' : 'Pembayaran dibatalkan'}
              text="Tagihan ini tidak bisa dibayar lagi. Buat tagihan baru bila masih ingin mengisi saldo."
            >
              <Link className="btn btn-primary" to="/wallet">
                Buat tagihan baru
              </Link>
            </Outcome>
          ) : view.status !== 'PENDING' ? (
            <Outcome icon="info" tone="neutral" title="Status pembayaran" text={view.status} />
          ) : view.qr_string ? (
            <div className="co-body">
              <div className="co-qr">
                <div className="co-qr-tile">
                  <QrCode value={view.qr_string} label={`Kode QRIS ${idr(view.amount)}`} />
                </div>
                <span className="co-qr-caption">QRIS · bisa dibayar dari semua bank dan e-wallet</span>
              </div>
              <button
                type="button"
                className="btn btn-block"
                onClick={() =>
                  void downloadQr(view.qr_string!, { amount: idr(view.amount), reference: view.external_payment_id, filename: `qris-${view.external_payment_id}.png` })
                }
              >
                <Icon name="image" size={15} />
                Unduh QR
              </button>
              <ol className="co-steps">
                {QRIS_STEPS.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>
          ) : view.va_number ? (
            <div className="co-body">
              <div className="co-va">
                <span className="co-va-bank">
                  <span className="co-bank-mark" aria-hidden>
                    {bank?.name.slice(0, 1) ?? 'V'}
                  </span>
                  {method}
                </span>
                <span className="co-va-label">Nomor virtual account</span>
                <span className="co-va-number">
                  <span data-testid="va-number">{groupDigits(view.va_number)}</span>
                  <CopyIcon value={view.va_number} label="Salin nomor virtual account" />
                </span>
                <span className="co-va-note">Transfer tepat {idr(view.amount)}. Nomor ini hanya untuk tagihan ini.</span>
              </div>
              {bank && (
                <div className="co-howto">
                  {bank.steps.map((group, i) => (
                    <details key={group.title} open={i === 0}>
                      <summary>
                        {group.title}
                        <Icon name="chevron" size={15} />
                      </summary>
                      <ol className="co-steps">
                        {group.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ol>
                    </details>
                  ))}
                </div>
              )}
            </div>
          ) : view.hosted_url ? (
            <Outcome icon="card" tone="neutral" title={`Bayar dengan ${method}`} text="Pembayaran diselesaikan di halaman mitra pembayaran kami. Saldo bertambah otomatis setelahnya.">
              <a className="btn btn-primary" href={view.hosted_url}>
                Lanjut bayar
                <Icon name="external" size={14} />
              </a>
            </Outcome>
          ) : (
            <Outcome icon="info" tone="neutral" title={`Bayar dengan ${method}`} text="Tagihan simulasi. Tekan Simulasikan bayar untuk menandainya lunas." />
          )}
        </Card>

        <Card className="co-side" title="Rincian">
          <dl className="co-rows">
            <div>
              <dt>Isi saldo</dt>
              <dd>{idr(view.amount)}</dd>
            </div>
            <div>
              <dt>Bonus saldo</dt>
              <dd className={view.bonus_amount ? 'bl-bonus' : undefined}>{view.bonus_amount ? `+${idr(view.bonus_amount)}` : '—'}</dd>
            </div>
            <div className="co-rows-total">
              <dt>Saldo masuk</dt>
              <dd>{idr(view.credited_amount)}</dd>
            </div>
            <div>
              <dt>Metode</dt>
              <dd>{method}</dd>
            </div>
            <div>
              <dt>Referensi</dt>
              <dd className="bl-id">
                <span className="truncate" style={{ maxWidth: 160 }} title={view.external_payment_id}>
                  {view.external_payment_id}
                </span>
                <CopyIcon value={view.external_payment_id} label="Salin referensi" />
              </dd>
            </div>
            {pending && view.expires_at && (
              <div>
                <dt>Bayar sebelum</dt>
                <dd>{dateTime(view.expires_at)}</dd>
              </div>
            )}
            {view.paid_at && (
              <div>
                <dt>Lunas</dt>
                <dd>{dateTime(view.paid_at)}</dd>
              </div>
            )}
          </dl>

          <div className={pending ? 'co-status' : 'co-status done'}>
            {pending ? (
              <>
                <span className="dot pulse" aria-hidden />
                Menunggu pembayaran
              </>
            ) : (
              <Badge status={view.status}>{view.status === 'PAID' ? 'Lunas' : view.status === 'EXPIRED' ? 'Kedaluwarsa' : 'Gagal'}</Badge>
            )}
          </div>

          {pending && (
            <div className="col">
              <button className="btn btn-primary btn-block" onClick={check} disabled={checking}>
                {checking ? <span className="spinner" /> : <Icon name="check" size={15} />}
                Saya sudah bayar
              </button>
              {view.can_simulate && (
                <button className="btn btn-block co-sim" onClick={simulate} disabled={simulating}>
                  {simulating ? <span className="spinner" /> : <Icon name="bolt" size={15} />}
                  Simulasikan bayar
                  <Badge tone="warn">Mode uji</Badge>
                </button>
              )}
            </div>
          )}
          <p className="field-hint co-fine">
            <Icon name="shield" size={13} />
            Pembayaran diproses aman. Tanpa biaya tambahan.
          </p>
        </Card>
      </div>
    </div>
  );
}

function Outcome({
  icon,
  tone,
  title,
  text,
  children,
}: {
  icon: 'success' | 'warn' | 'info' | 'card';
  tone: 'good' | 'warn' | 'neutral';
  title: string;
  text: string;
  children?: ReactNode;
}) {
  return (
    <div className={`co-outcome ${tone}`}>
      <span className="co-outcome-icon">
        <Icon name={icon} size={26} />
      </span>
      <h2>{title}</h2>
      <p>{text}</p>
      {children}
    </div>
  );
}
